import React from 'react'
import { Helmet } from 'react-helmet'
import { RouteComponentProps } from 'react-router'

import bootcampLogo from 'assets/bootcamp-login-logo.svg'
import Image from 'components/common/Image'
import SearchInput from 'components/common/SearchInput'
import Section from 'components/common/Section'
import Text from 'components/common/Text'
import TeamSelect from 'components/rbac-settings/TeamSelect'
import useAgent, { MaybeArrayValue, UseAgentError } from 'hooks/use-agent'
import useSearch from 'hooks/use-search'
import theme from 'theme'
import {
  JoinableBootcampResponse,
  JoinableBootcampTeam
} from 'types/api/bootcamps/joinable-bootcamp'
import { naturalSortBy } from 'utils/list'
import { messageFromUseAgentError } from 'utils/strings'

import JoinBootcampConfirmationModal from './JoinBootcampConfirmationModal'

type JoinBootcampParams = {
  companyId: string
}

const JoinBootcamp: React.FC<RouteComponentProps<JoinBootcampParams>> = ({
  match
}) => {
  const companyId = match.params.companyId

  const params = new URLSearchParams(location.search)
  const bootcampCode = params.get('code')

  const [searchValue, setSearchValue] = React.useState('')
  const [team, setTeam] = React.useState<JoinableBootcampTeam>(
    {} as JoinableBootcampTeam
  )
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const {
    data: joinableBootcamp,
    isLoading: isLoadingJoinableBootcamp,
    hasError: hasErrorJoinableBootcamp,
    error: getJoinableBootcampError
  }: {
    data: JoinableBootcampResponse
    isLoading: boolean
    hasError: boolean
    error: MaybeArrayValue<UseAgentError, JoinableBootcampResponse>
  } = useAgent<JoinableBootcampResponse>(
    `/bootcamps/${companyId}/${bootcampCode}`,
    null,
    {}
  )

  const joinableTeams = joinableBootcamp?.teams

  const searchMapFn = React.useCallback(
    (team: JoinableBootcampTeam) =>
      [team.name, team.identifier].filter(Boolean),
    []
  )
  const searchTeams = useSearch(joinableTeams, searchMapFn)

  return (
    <main
      css={{
        display: 'block',
        margin: 'auto',
        maxWidth: theme.space[10],
        paddingTop: theme.space[7],
        paddingBottom: theme.space[7]
      }}
    >
      <div
        css={{
          display: 'block',
          margin: 'auto',
          maxWidth: theme.space[10],
          paddingTop: theme.space[5],
          paddingBottom: theme.space[5],
          paddingLeft: theme.space[7],
          paddingRight: theme.space[7],
          border: theme.border.base,
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius[1]
        }}
      >
        <Section>
          <Helmet title="Join Bootcamp" />
          <Image alt="Gremlin" height={theme.space[6]} src={bootcampLogo} />
          <Text
            as="h2"
            css={{
              marginTop: theme.space[3],
              marginBottom: theme.space[1]
            }}
          >
            {joinableBootcamp?.name}
          </Text>
          <Text
            as="small"
            css={{ marginTop: theme.space[1], marginBottom: theme.space[4] }}
          >
            {joinableBootcamp?.companyName}
          </Text>
        </Section>
        <Section
          errorMessage={
            Array.isArray(getJoinableBootcampError)
              ? messageFromUseAgentError(
                  getJoinableBootcampError[0],
                  'Unable to fetch bootcamp data'
                )
              : messageFromUseAgentError(
                  getJoinableBootcampError,
                  'Unable to fetch bootcamp data'
                )
          }
          hasError={hasErrorJoinableBootcamp}
          isLoading={isLoadingJoinableBootcamp}
        >
          <Text
            as="h4"
            css={{
              fontWeight: theme.fontWeights[0],
              marginTop: theme.space[3],
              marginBottom: theme.space[1]
            }}
          >
            Welcome to the bootcamp! Please select a team to join in the
            bootcamp's company.
          </Text>
          <SearchInput
            data-test-bootcamp-teams-search
            search
            css={{
              marginTop: theme.space[4],
              marginBottom: theme.space[3]
            }}
            label="Search Teams"
            maxWidth="auto"
            name="bootcamp-teams-search"
            type="text"
            value={typeof searchValue !== 'string' ? '' : searchValue}
            onChange={({ target: { value } }) => setSearchValue(value)}
          />

          <TeamSelect
            getTeamFromTeams={() => {
              // Sort bootcamps by search value
              return naturalSortBy<JoinableBootcampResponse>(
                searchTeams(searchValue),
                [(key) => key.name]
              )
            }}
            multiSelect={false}
            teams={joinableTeams}
            onTeamClick={(selectedTeam) => {
              setTeam(selectedTeam)
              setIsModalOpen(true)
            }}
          />
        </Section>
      </div>
      <JoinBootcampConfirmationModal
        bootcampCode={bootcampCode}
        companyId={companyId}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        team={team}
      />
    </main>
  )
}

export default JoinBootcamp
